import { t, Trans } from "@lingui/macro";
import { Grid, Typography } from "@mui/material";
import { MatesSelect, MatesTextField, withUseFormHook } from "@sinossi/mates-react-library";
import React from "react";
import { UseFormReturn } from "react-hook-form";
import * as Yup from "yup";
import { AccountType, LandLordType, NewUserRequest } from "../../../models";
import { SbFormDialog } from "../../commons/sb-form-dialog";
import MultipleBrandAutocomplete from "./multiple-brand-autocomplete";
import { MultipleBrandRole } from "./multiple-brand-role";

interface NewUserModalProps extends UseFormReturn {
    onClose: () => void
    submit: (request: NewUserRequest) => void
}

interface NewUserModalState {
    isBrand: boolean
    isLandlord: boolean
    landlordType: any
    brandSelected: { id: string, name: string }[]
    brandRoles: { brandId: string, job: string }[]
    accordionColor: string
    autocompleteColor: string
}

const NewUserModalSchema = Yup.object().shape({
    username: Yup.string().required("Il campo è obbligatorio."),
    accountType: Yup.string().required("Il campo è obbligatorio."),
    firstName: Yup.string().required("Il campo è obbligatorio."),
    brands: Yup.string().when("accountType", {
        is: (type: string) => type as AccountType === AccountType.BRAND,
        then: (schema: any) => Yup.boolean().isTrue().required()
    }
    ),
    jobs: Yup.string().when("accountType", {
        is: (type: string) => type as AccountType === AccountType.BRAND,
        then: (schema: any) => Yup.boolean().isTrue().required()
    }
    ),
})

class NewUserModal extends React.Component<NewUserModalProps, NewUserModalState> {

    constructor(props: NewUserModalProps) {
        super(props)
        this.state = {
            isBrand: false,
            isLandlord: false,
            landlordType: null,
            brandSelected: [],
            brandRoles: [],
            autocompleteColor: "",
            accordionColor: "primary.main"
        }
    }

    componentDidMount() {
        this.props.setValue("accountType", AccountType.ADMIN)
        this.props.watch((value, { name }) => this.checkIfBrand(name))
    }

    private checkIfBrand = (name: string | undefined) => {
        if (name && name === "accountType") {
            this.setState({
                isBrand: this.props.getValues(name) as AccountType === AccountType.BRAND,
                isLandlord: this.props.getValues(name) as AccountType === AccountType.LANDLORD
            })
        }
        else if (name && name.includes("multiple-brand-role.")) {
            this.updateRoles(name)
        }
    }

    private submit = () => {
        let request: NewUserRequest = {
            username: this.props.getValues("username"),
            accountType: this.props.getValues("accountType"),
            firstName: this.props.getValues("firstName"),
            lastName: this.props.getValues("lastName"),
            address: this.props.getValues("address"),
            city: this.props.getValues("city"),
            province: this.props.getValues("province"),
            postalCode: this.props.getValues("postalCode"),
            country: this.props.getValues("country"),
            phone: this.props.getValues("phone"),
            vatCode: this.props.getValues("vatCode"),
            fiscalCode: this.props.getValues("fiscalCode")
        }


        if (this.state.isBrand) {
            request = { ...request, brandData: this.state.brandRoles }
        }

        if (this.state.isLandlord) {
            request = { ...request, landlordType: this.props.getValues("landlordType") }
        }

        this.props.submit(request)
    }

    private validationError = (values: any) => {
        let accordion: string = "primary.main"
        let autocomplete: string = ""

        if (values.hasOwnProperty("brands")) {
            autocomplete = "red"
            accordion = "red"
        }

        if (values.hasOwnProperty("jobs")) {
            accordion = "red"
        }

        this.setState(() => ({
            accordionColor: accordion,
            autocompleteColor: autocomplete
        }))
    }

    private updateBrands = (brands: { id: string, name: string }[]) => {
        let color = ""

        this.props.setValue("brands", brands.length !== 0)

        if (brands.length === 0) {
            color = "red"
        }

        this.setState(() => ({
            brandSelected: brands,
            autocompleteColor: color
        }))
    }

    private updateRoles = (fieldName: string) => {
        const brandId = fieldName.split(".")[1]
        const job = this.props.getValues(fieldName)

        let newBrandRoles = this.state.brandRoles
        let isInit: boolean = false

        if (this.state.brandRoles.length > 0) {
            this.state.brandRoles.forEach((item) => {
                if (item.brandId === brandId) {
                    isInit = true
                    item.job = job
                    return
                }
            })
        }

        if (!isInit) {
            newBrandRoles.push({ brandId: brandId, job: job })
        }

        this.props.setValue("jobs", newBrandRoles.length === this.state.brandSelected.length)

        this.setState(({
            brandRoles: newBrandRoles
        }))
    }

    render() {
        return (
            <SbFormDialog {...this.props}
                onClose={this.props.onClose}
                width={"lg"}
                titleText={t({ id: "new-user-modal.title", message: "Creazione utente" })}
                closeButton
                submitButton
                schema={NewUserModalSchema}
                onSubmit={this.submit}
                validationError={this.validationError}
            >
                <Grid container columnSpacing={3} rowSpacing={1}>
                    <Grid item xs={12}>
                        <Typography fontWeight={400} color={"primary"} sx={{ pt: 0 }}>
                            <Trans id={"new-user-modal.user-data"}>
                                Tipo utente e dati anagrafici
                            </Trans>
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <MatesSelect
                            name="accountType"
                            label={t({ id: "new-user-account-type", message: "Tipo di utente" })}
                            variant="outlined"
                            formState={this.props.formState}
                            control={this.props.control}
                            trigger={this.props.trigger}>
                            {Object.keys(AccountType).map((typeKey, index) =>
                                <option value={typeKey} key={index}>
                                    <Trans id={`enums.account-type.${typeKey}`}>
                                    </Trans>
                                </option>
                            )}
                        </MatesSelect>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <MatesTextField
                            name="username"
                            label={t({ id: "new-user-email", message: "Email" })}
                            variant="outlined"
                            control={this.props.control}
                            trigger={this.props.trigger}
                            formState={this.props.formState} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <MatesTextField
                            name="firstName"
                            label={t({ id: "new-user-first-name-or-denomination", message: "Nome (o denominazione" })}
                            variant="outlined"
                            control={this.props.control}
                            trigger={this.props.trigger}
                            formState={this.props.formState} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <MatesTextField
                            name="lastName"
                            label={t({ id: "new-user-last-name", message: "Cognome" })}
                            variant="outlined"
                            control={this.props.control}
                            trigger={this.props.trigger}
                            formState={this.props.formState} />
                    </Grid>
                    <Grid item xs={12} mt={2}>
                        <Typography fontWeight={400} color={"primary"}>
                            <Trans id={"new-user-modal.address"}>
                                Indirizzo di riferimento
                            </Trans>
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <MatesTextField
                            name="address"
                            label={t({ id: "new-user-address", message: "Indirizzo" })}
                            variant="outlined"
                            control={this.props.control}
                            trigger={this.props.trigger}
                            formState={this.props.formState} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <MatesTextField
                            name="city"
                            label={t({ id: "new-user-city", message: "Città" })}
                            variant="outlined"
                            control={this.props.control}
                            trigger={this.props.trigger}
                            formState={this.props.formState} />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <MatesTextField
                            name="province"
                            label={t({ id: "new-user-province", message: "Provincia" })}
                            variant="outlined"
                            control={this.props.control}
                            trigger={this.props.trigger}
                            formState={this.props.formState} />
                    </Grid>
                    <Grid item xs={12} md={1}>
                        <MatesTextField
                            name="postalCode"
                            label={t({ id: "new-user-postal-code", message: "CAP" })}
                            variant="outlined"
                            control={this.props.control}
                            trigger={this.props.trigger}
                            formState={this.props.formState} />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <MatesTextField
                            name="country"
                            label={t({ id: "new-user-state", message: "Stato" })}
                            variant="outlined"
                            control={this.props.control}
                            trigger={this.props.trigger}
                            formState={this.props.formState} />
                    </Grid>
                    <Grid item xs={12} mt={2}>
                        <Typography fontWeight={400} color={"primary"}>
                            <Trans id={"new-user-modal.more-data"}>
                                Altri dati
                            </Trans>
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <MatesTextField
                            name="phone"
                            label={t({ id: "new-user-phone", message: "Numero di telefono" })}
                            variant="outlined"
                            control={this.props.control}
                            trigger={this.props.trigger}
                            formState={this.props.formState} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <MatesTextField
                            name="vatCode"
                            label={t({ id: "new-user-vat-code", message: "Partita iva" })}
                            variant="outlined"
                            control={this.props.control}
                            trigger={this.props.trigger}
                            formState={this.props.formState} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <MatesTextField
                            name="fiscalCode"
                            label={t({ id: "new-user-fiscal-code", message: "Codice fiscale" })}
                            variant="outlined"
                            control={this.props.control}
                            trigger={this.props.trigger}
                            formState={this.props.formState} />
                    </Grid>
                    {
                        this.state.isLandlord &&
                        <>
                            <Grid item xs={12} mt={2}>
                                <Typography fontWeight={400} color={"primary"}>
                                    <Trans id={"new-user-modal.landlord-details"}>
                                        Dettagli Landlord
                                    </Trans>
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <MatesSelect
                                    name="landlordType"
                                    label={t({ id: "new-user-landlord-type", message: "Tipologia landlord" })}
                                    variant="outlined"
                                    formState={this.props.formState}
                                    control={this.props.control}
                                    trigger={this.props.trigger}>
                                    {Object.keys(LandLordType).map((typeKey, index) =>
                                        <option value={typeKey} key={index}>
                                            <Trans id={`enums.landlord-type.${typeKey}`}>
                                            </Trans>
                                        </option>
                                    )}
                                </MatesSelect>
                            </Grid>
                        </>
                    }
                    {
                        this.state.isBrand &&
                        <>
                            <Grid item xs={12} mt={2}>
                                <Typography fontWeight={400} color={"primary"}>
                                    <Trans id={"new-user-modal.brand-details"}>
                                        Dettagli brand di riferimento
                                    </Trans>
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <MultipleBrandAutocomplete {...this.props} updateBrands={this.updateBrands} color={this.state.autocompleteColor} />
                            </Grid>
                            <Grid item xs={12}>
                                <MultipleBrandRole {...this.props} brands={this.state.brandSelected} updateRoles={this.updateRoles} color={"secondary.main"} />
                            </Grid>
                        </>

                    }
                </Grid>
            </SbFormDialog>
        )
    }
}

export default withUseFormHook<{}>(
    NewUserModal,
    NewUserModalSchema
)
